import { Link } from "react-router-dom";
import { Calendar, Star } from "lucide-react";
import type { DailyJournal } from "@/types";

interface JournalPreviewProps {
  journal: DailyJournal;
  className?: string;
}

/**
 * Preview card for a daily journal
 * Shows date, importance, and summary snippet
 *
 * @param journal - Journal data to display
 * @param className - Additional CSS classes
 * @returns Compact journal preview card
 */
export function JournalPreview({ journal, className = "" }: JournalPreviewProps) {
  // Format date for display
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("ja-JP", {
      month: "short",
      day: "numeric",
      weekday: "short",
    });
  };

  // Truncate summary
  const truncateSummary = (text: string, maxLength: number = 80) => {
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength).trim() + "...";
  };

  return (
    <Link
      to={`/journals/${journal.date}`}
      className={`
        block bg-white rounded-xl border p-4
        hover:shadow-md hover:border-primary-200
        transition-all duration-200 animate-fade-in
        ${className}
      `}
    >
      <div className="flex items-center gap-2 text-slate-500 mb-2">
        <Calendar size={14} />
        <span className="text-sm">{formatDate(journal.date)}</span>
        <div className="flex items-center gap-0.5 ml-auto">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star
              key={i}
              size={12}
              className={
                i < journal.importanceScore
                  ? "text-accent-500 fill-accent-500"
                  : "text-slate-200"
              }
            />
          ))}
        </div>
      </div>

      <p className="text-sm text-slate-700 leading-relaxed">
        {truncateSummary(journal.overallSummary)}
      </p>
    </Link>
  );
}

